/**
 * IdentityResultCard — Rudrik
 * ===========================
 * Single, shared "identity result" surface used across every module that
 * screens a person or entity (PAN, Aadhaar, Face Match, 360°, Sanctions,
 * Court Records, ED/CBI/SFIO, Watchlist, Screening, Final Report).
 *
 *  All six standard sections ALWAYS render, even when a field is missing —
 *  the analyst needs to see "Not available" so they know the data was
 *  CHECKED and came back empty, not that it was never checked.
 *
 *    1. Identity Header  (photo · name · aliases · risk badge)
 *    2. Personal Details (DOB · place · age · gender · nationality · parent)
 *    3. Identification   (PAN · Aadhaar last4 · passport · voter · DIN/CIN)
 *    4. Address          (line / city / state / pincode / country)
 *    5. Source           (provenance · timestamp · confidence)
 *    6. Risk Flags       (severity-coded list with one-line reasons)
 *
 *  Optional module-specific UI is rendered as a `children` slot below the
 *  6 standard sections, so each module keeps its own deep-dive view while
 *  the IDENTITY portion is unified.
 *
 *  Input shape (see lib/identityNormalizers.js for per-source mappers):
 *    person = {
 *      photo, name, aliases, risk_level,
 *      dob, place_of_birth, age, gender, nationality, parent_or_spouse,
 *      pan, aadhaar_masked, passport, voter_id, din_cin,
 *      address: { line, city, state, pincode, country },
 *      source: { label, fetched_at, confidence, kind },  // kind: official|llm|news
 *      risk_flags: [{ severity, label, detail }],
 *    }
 */
import React from "react";
import {
  ShieldCheck, ShieldAlert, User, Calendar, MapPin, IdCard, Flag,
  AlertTriangle, Sparkles, Globe, Users, FileText, Camera,
} from "lucide-react";

const RISK_COLOR = {
  LOW: "#22c55e",
  MEDIUM: "#f59e0b",
  HIGH: "#ef4444",
  CRITICAL: "#dc2626",
  WARNING: "#f59e0b",
  CLEAR: "#22c55e",
  UNKNOWN: "#94a3b8",
};

const SEV_COLOR = {
  critical: "#dc2626", CRITICAL: "#dc2626",
  high: "#ef4444",     HIGH: "#ef4444",
  medium: "#f59e0b",   MEDIUM: "#f59e0b",
  low: "#22c55e",      LOW: "#22c55e",
  warning: "#f59e0b",  WARNING: "#f59e0b",
};

const NA = "Not available";

/* ---------- helpers ---------- */
const isPresent = (v) =>
  v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0);

function calcAge(dob) {
  if (!dob) return null;
  // accept DD/MM/YYYY or YYYY-MM-DD
  let d, m, y;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) [d, m, y] = dob.split("/").map(Number);
  else if (/^\d{4}-\d{2}-\d{2}/.test(dob)) [y, m, d] = dob.slice(0, 10).split("-").map(Number);
  else return null;
  const birth = new Date(y, m - 1, d);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const mDiff = today.getMonth() - birth.getMonth();
  if (mDiff < 0 || (mDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

function fmtTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

/* ---------- sub-components ---------- */

function Field({ label, value, mono, na = NA, testId }) {
  const v = isPresent(value) ? value : na;
  const missing = !isPresent(value);
  return (
    <div data-testid={testId}>
      <div style={{
        color: "#64748b", fontSize: 10, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4,
      }}>{label}</div>
      <div style={{
        color: missing ? "#475569" : "#f1f5f9",
        fontSize: 13, fontWeight: 600,
        fontStyle: missing ? "italic" : "normal",
        fontFamily: mono ? "ui-monospace, SFMono-Regular, monospace" : "inherit",
        wordBreak: "break-word",
      }}>{v}</div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6, marginBottom: 10,
      color: "#64748b", fontSize: 10, fontWeight: 700,
      textTransform: "uppercase", letterSpacing: 0.7,
    }}>
      <Icon size={11} /> {title}
    </div>
  );
}

function Photo({ src, name }) {
  const initials = (name || "?")
    .split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  if (src) {
    const url = src.startsWith("data:") ? src
              : src.startsWith("http") ? src
              : `data:image/jpeg;base64,${src}`;
    return (
      <img src={url} alt={name || "identity photo"}
           data-testid="identity-photo"
           style={{
             width: 88, height: 88, borderRadius: 8, objectFit: "cover",
             border: "1px solid #1e2530", flexShrink: 0,
           }} />
    );
  }
  return (
    <div data-testid="identity-photo-placeholder"
         style={{
           width: 88, height: 88, borderRadius: 8, flexShrink: 0,
           background: "#080c12", border: "1px dashed #1e2530",
           display: "flex", alignItems: "center", justifyContent: "center",
           flexDirection: "column", gap: 4,
         }}>
      {initials ? (
        <span style={{ color: "#475569", fontSize: 22, fontWeight: 800,
                       fontFamily: "ui-monospace, monospace" }}>{initials}</span>
      ) : (
        <User size={28} style={{ color: "#334155" }} />
      )}
      <span style={{ color: "#475569", fontSize: 8, fontWeight: 700,
                     textTransform: "uppercase", letterSpacing: 0.6 }}>No photo</span>
    </div>
  );
}

function RiskBadge({ level }) {
  const norm = (level || "UNKNOWN").toString().toUpperCase();
  const color = RISK_COLOR[norm] || "#94a3b8";
  return (
    <div data-testid="identity-risk-badge"
         style={{
           background: `${color}1f`, color, border: `1px solid ${color}66`,
           padding: "5px 12px", borderRadius: 999,
           fontSize: 11, fontWeight: 800, letterSpacing: 0.6,
           textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 6,
           whiteSpace: "nowrap",
         }}>
      <ShieldAlert size={12} /> {norm}
    </div>
  );
}

function AliasChips({ aliases }) {
  if (!Array.isArray(aliases) || aliases.length === 0) return null;
  return (
    <div data-testid="identity-aliases"
         style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
      <span style={{
        color: "#64748b", fontSize: 10, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.5, alignSelf: "center",
      }}>AKA:</span>
      {aliases.slice(0, 6).map((a, i) => (
        <span key={`${a}-${i}`}
              style={{
                background: "#0d1117", border: "1px solid #1e2530", color: "#cbd5e1",
                padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                fontFamily: "ui-monospace, monospace",
              }}>{a}</span>
      ))}
      {aliases.length > 6 && (
        <span style={{ color: "#64748b", fontSize: 10, alignSelf: "center" }}>
          +{aliases.length - 6} more
        </span>
      )}
    </div>
  );
}

/* ---------- main component ---------- */

export default function IdentityResultCard({ person = {}, children, dense = false }) {
  const {
    photo, name, aliases = [], risk_level,
    dob, place_of_birth, gender, nationality, parent_or_spouse,
    pan, aadhaar_masked, passport, voter_id, din_cin,
    address = {},
    source = {},
    risk_flags = [],
  } = person;

  const age = calcAge(dob);

  return (
    <div
      data-testid="identity-result-card"
      style={{
        background: "#0d1117",
        border: `1px solid ${RISK_COLOR[(risk_level || "UNKNOWN").toString().toUpperCase()] || "#1e2530"}55`,
        borderRadius: 12,
        padding: dense ? 16 : 22,
        color: "#f1f5f9",
      }}
    >
      {/* 1. IDENTITY HEADER */}
      <div style={{ display: "flex", gap: 16, marginBottom: 18 }}>
        <Photo src={photo} name={name} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div data-testid="identity-name"
                   style={{
                     fontSize: 22, fontWeight: 800, lineHeight: 1.2,
                     color: isPresent(name) ? "#f1f5f9" : "#475569",
                     fontStyle: isPresent(name) ? "normal" : "italic",
                     wordBreak: "break-word",
                   }}>
                {isPresent(name) ? name : NA}
              </div>
              <AliasChips aliases={aliases} />
            </div>
            <RiskBadge level={risk_level} />
          </div>
        </div>
      </div>

      {/* 2. PERSONAL DETAILS GRID */}
      <section data-testid="identity-personal"
               style={{ background: "#080c12", border: "1px solid #1e2530",
                        borderRadius: 8, padding: 14, marginBottom: 12 }}>
        <SectionHeader icon={User} title="Personal Details" />
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 14,
        }}>
          <Field label="Date of Birth" value={dob} testId="field-dob" />
          <Field label="Place of Birth" value={place_of_birth} testId="field-pob" />
          <Field label="Age" value={age != null ? `${age} yrs` : null} testId="field-age" />
          <Field label="Gender" value={gender} testId="field-gender" />
          <Field label="Nationality" value={nationality} testId="field-nationality" />
          <Field label="Father / Spouse" value={parent_or_spouse} testId="field-parent" />
        </div>
      </section>

      {/* 3. IDENTIFICATION NUMBERS */}
      <section data-testid="identity-ids"
               style={{ background: "#080c12", border: "1px solid #1e2530",
                        borderRadius: 8, padding: 14, marginBottom: 12 }}>
        <SectionHeader icon={IdCard} title="Identification Numbers" />
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 14,
        }}>
          <Field label="PAN" value={pan} mono testId="field-pan" />
          <Field label="Aadhaar (last 4)" value={aadhaar_masked} mono testId="field-aadhaar" />
          <Field label="Passport" value={passport} mono testId="field-passport" />
          <Field label="Voter ID / DL" value={voter_id} mono testId="field-voter" />
          <Field label="DIN / CIN" value={din_cin} mono testId="field-din" />
        </div>
      </section>

      {/* 4. ADDRESS */}
      <section data-testid="identity-address"
               style={{ background: "#080c12", border: "1px solid #1e2530",
                        borderRadius: 8, padding: 14, marginBottom: 12 }}>
        <SectionHeader icon={MapPin} title="Address" />
        {(() => {
          const parts = [address.line, address.city, address.state,
                         address.pincode, address.country].filter(isPresent);
          if (parts.length === 0) {
            return <div style={{ color: "#475569", fontSize: 13, fontStyle: "italic" }}>{NA}</div>;
          }
          return (
            <div data-testid="identity-address-text"
                 style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.7 }}>
              {parts.join(", ")}
            </div>
          );
        })()}
      </section>

      {/* 6. RISK FLAGS (shown above source for prominence) */}
      {Array.isArray(risk_flags) && risk_flags.length > 0 && (
        <section data-testid="identity-flags"
                 style={{ background: "#080c12", border: "1px solid #1e2530",
                          borderRadius: 8, padding: 14, marginBottom: 12 }}>
          <SectionHeader icon={AlertTriangle} title={`Risk Flags (${risk_flags.length})`} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {risk_flags.map((f, i) => {
              const sev = (f.severity || "medium").toString();
              const c = SEV_COLOR[sev] || "#f59e0b";
              return (
                <div key={`${f.label || f.type}-${i}`}
                     data-testid={`identity-flag-${i}`}
                     style={{
                       background: `${c}10`, border: `1px solid ${c}44`,
                       borderRadius: 6, padding: "8px 12px",
                     }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <AlertTriangle size={12} style={{ color: c, flexShrink: 0 }} />
                    <span style={{ color: c, fontSize: 12, fontWeight: 700 }}>
                      {f.label || f.title || f.type || "Flag"}
                    </span>
                    <span style={{
                      marginLeft: "auto", color: c, fontSize: 9, fontWeight: 800,
                      textTransform: "uppercase", letterSpacing: 0.5,
                    }}>{sev}</span>
                  </div>
                  {(f.detail || f.reason) && (
                    <div style={{ color: "#cbd5e1", fontSize: 11, marginTop: 4, marginLeft: 20 }}>
                      {f.detail || f.reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* MODULE-SPECIFIC SLOT */}
      {children && (
        <section data-testid="identity-module-extras" style={{ marginBottom: 12 }}>
          {children}
        </section>
      )}

      {/* 5. SOURCE & PROVENANCE — last, small */}
      <div data-testid="identity-source"
           style={{
             borderTop: "1px solid #1e2530", paddingTop: 10, marginTop: 4,
             display: "flex", flexWrap: "wrap", gap: 12,
             color: "#64748b", fontSize: 10, lineHeight: 1.5,
           }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Sparkles size={9} /> Source: {isPresent(source.label) ? source.label : "Unknown"}
        </span>
        {isPresent(source.fetched_at) && (
          <span>Fetched: {fmtTime(source.fetched_at)}</span>
        )}
        {isPresent(source.confidence) && (
          <span>Confidence: <span style={{ color: "#cbd5e1", fontWeight: 700 }}>
            {source.confidence}
          </span></span>
        )}
        {source.kind === "llm" && (
          <span style={{ color: "#fbbf24", fontWeight: 700 }}>
            ⚠ LLM-extracted — unverified, not an official KYC source
          </span>
        )}
        {source.kind === "news" && (
          <span style={{ color: "#fbbf24", fontWeight: 700 }}>
            ⚠ Extracted from press release — verify with primary source
          </span>
        )}
      </div>
    </div>
  );
}

/* re-export helpers for normalizers */
export { isPresent, calcAge };
